import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ArrayHelper } from "apps/idl/src/helpers";
import { ConfigData, EDatabaseRole, ESecurityFlag, IHttpError, IWorkspace, UserData } from "apps/idl/src/model";
import { motsDePasses } from "apps/idl/src/modules/password/french_passwords";
import { PasswordService } from "apps/idl/src/modules/password/services/password.service";
import { EPermissionsFlag } from "apps/idl/src/modules/permissions/models/EPermissionsFlag";
import { ISite } from "apps/idl/src/modules/sites/models/isite";
import { SitesService } from "apps/idl/src/modules/sites/services/sites.service";
import { ContactsService, FlagService, PlatformService, SecurityService, Store, WorkspaceService } from "apps/idl/src/services/security";
import { EMPTY, Observable, of, throwError } from "rxjs";
import { catchError, finalize, map, mergeMap, tap } from "rxjs/operators";
import { C_PREFIX_WS } from "../../../anakin.constants";
import { SceEventService } from "../../sce4Mobile/sce-event.service";
import { AuthenticatorService } from "../../shared/services/authenticator.service";
import { LoaderService } from "../../shared/services/loader.service";

@Injectable({
	providedIn: "root"
})
export class ConnexionService {

	public workspaceId: string = "";
	response: string = '';

	constructor(
		private svcPassword: PasswordService,
		private svcWorkspace: WorkspaceService,
		private svcSites: SitesService,
		private router: Router,
		private svcAuthenticator: AuthenticatorService,
		private svcSecurity: SecurityService,
		private svcFlag: FlagService,
		private svcLoader: LoaderService,
		private svcApp : PlatformService,
		private svcSceEvent : SceEventService,
		private svcContact: ContactsService,
		private svcStore: Store

	) {
		ConfigData.appInfo.versionAppErgo = localStorage.getItem("versionAppErgo");
	}

	public waitForConnexionFlags() {
		this.svcFlag.waitForFlags(
			[ESecurityFlag.authenticated,
			EPermissionsFlag.isLoaded
			], true)
			.pipe(
				tap(() => this.svcLoader.showLoader("Chargement de l'espace de travail...", "Blanc")),
				mergeMap(() => {
					if (!this.svcApp.isMobileApp) return of(null);

					let dbId: string | null = null;
					try {
						const dbIds = this.svcStore.getDatabasesIdsByRole(EDatabaseRole.workspace);
						dbId = ArrayHelper.getFirstElement(dbIds);
					} catch (e) {
						console.debug("Aucune base workspace trouvée, réplication du document workspace ignorée", e);
					}

					if (dbId) {
						return this.svcStore.replicateToLocal(dbId, {
							selector: {
								_id: {
									$gte: `${C_PREFIX_WS}`,
									$lt: `${C_PREFIX_WS}\ufff0`
								}
							},
							since: '0'
						}).pipe(
							catchError(err => {
								console.error('Erreur durant la réplication vers la base locale', err);
								return of(null); 
							})
						);
					}
					return of(null);
				}),
				mergeMap(() =>
					this.controleSiteWorkspace(this.workspaceId)
				),
				finalize(() => this.svcLoader.hideLoader()))
			.subscribe();
	}

	public async sendMailRequest(email: string): Promise<IHttpError | void> {
		if (!email) return Promise.resolve({ message: 'Veuillez entrer une adresse email valide.' } as IHttpError);

		try {
			await this.svcPassword.sendResetPasswordEmail(email)
				.pipe(
					tap(() => {
						this.router.navigate(['/confirmationEmail'], { queryParams: { email: email } });
					})
				)
				.toPromise();
			return null;
		} catch (error) {
			return error as IHttpError;
		}
	}

	public async verifToken(token: string): Promise<IHttpError | void> {

		if (!token) this.router.navigate(['/lienExpire']);

		try {
			const result = await this.svcPassword.verifToken(token)
				.pipe(
					tap((res: boolean) => {
						if (res)
							this.router.navigate(['/lienExpire']);
					})
				)
				.toPromise();
			return null;
		} catch (error) {
			return error as IHttpError;
		}
	}


	public isValidCriters(password: string, isReset: boolean): string[] | null {
		const validationErrors: string[] = [];

		const regexNumber: RegExp = /\d/;
		const regexMajuscule: RegExp = /[A-Z]/;
		const regexMinuscule: RegExp = /[a-z]/;
		const regexSize: RegExp = /^[^\s]{8,}$/;
		const regexSpecialCar: RegExp = /[!@#$%^&*()_+{}\[\]:;<>,.?~\\|]/;

		if (password.match(regexNumber) !== null) {
			validationErrors.push('notNumberValid');
		}
		if (password.match(regexMajuscule) !== null) {
			validationErrors.push('notMajusculeValid');
		}
		if (password.match(regexMinuscule) !== null) {
			validationErrors.push('notMinusculeValid');
		}
		if (password.match(regexSize) !== null) {
			validationErrors.push('notSizeValid');
		}
		if (password.match(regexSpecialCar) !== null) {
			validationErrors.push('notSpecialCarValid');
		}
		if (password.toLowerCase().includes(ConfigData.appInfo.login.toLowerCase())) {
			if (!isReset) {
				validationErrors.push('notDifLoginValid');
			}
		}
		if (!motsDePasses.includes(password)) {
			validationErrors.push('notCourantValid');
		}

		return validationErrors;
	}

	private hasMaxTwoOccurrencesOfEachLetter(mot: string): boolean {
		for (let i = 0; i < mot.length - 2; i++) {
			if (mot[i] === mot[i + 1] && mot[i] === mot[i + 2]) {
				return false;
			}
		}
		return true;
	}

	public hasError(errors: string[], errorCode: string): boolean {
		return !errors.includes(errorCode);
	}

	public async selectWorkspace(workspaceId: string) {
		this.workspaceId = workspaceId;
		if (this.svcApp.isMobileApp && !ConfigData.sceInitialize)
			await this.svcSceEvent.initialize();
		this.svcSecurity.setAuthenticationStatus(true);
	}


	public controleSiteWorkspace(workspaceId: string): Observable<ISite | Error> {
		let lsDefaultSiteId: string;
		return this.svcWorkspace.getWorkspaceDocument(workspaceId).
			pipe(
				tap((poWorkspace: IWorkspace) => {
					lsDefaultSiteId = poWorkspace.defaultSiteId;
					UserData.modules = poWorkspace.modules ?? [];
				}),
				mergeMap(() => this.svcSites.getUserSiteIds()),
				map((cabinetlistId: string[]) => lsDefaultSiteId ? ArrayHelper.unique([lsDefaultSiteId, ...cabinetlistId]) : cabinetlistId),
				mergeMap((cabinetlistId: string[]) => {
					if (cabinetlistId.length === 0) {
						return throwError(new Error('Aucun cabinet pour ce workspace.'));
					}

					if (cabinetlistId.length === 1) {
						const cabinetId: string = ArrayHelper.getFirstElement(cabinetlistId);
						return this.svcSites.getSite(cabinetId).pipe(
							tap((poSite: ISite) => {
								UserData.currentSite = poSite;
								UserData.currentSite.isDefaultSite = poSite._id === lsDefaultSiteId;
								this.svcContact.initZoneGeo("");
								this.svcAuthenticator.setAuthenticationStatus(true);
								this.router.navigate(["/home"]);
							})
						);
					}

					if (cabinetlistId.length > 1) {
						this.router.navigate(['/selectionCabinet'], { queryParams: { cabinetlistId: cabinetlistId } });
						return EMPTY;
					}

					return throwError(new Error('Chemin inattendu'));
				})
			);
	}
}